import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type TemplateVariableKey, TemplateVariableButtons } from './template-variables';

import { RichTextEditor, type RichTextEditorHandle } from '~/components/common/rich-text-editor';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';

const EMAIL_VARIABLES: TemplateVariableKey[] = [
  'applicantName',
  'jobTitle',
  'jobEmployer',
  'jobLocation',
  'jobDescription',
  'inviteUrl'
];

interface Props {
  subject: string;
  body: string;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  disabled?: boolean;
}

export function EmailEditor({
  subject,
  body,
  onSubjectChange,
  onBodyChange,
  disabled
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const editorRef = useRef<RichTextEditorHandle>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const [lastFocused, setLastFocused] = useState<'subject' | 'body'>('body');

  function insertIntoBody(variable: string): void {
    editorRef.current?.insertContent(variable);
  }

  function insertIntoSubject(variable: string): void {
    const el = subjectRef.current;
    if (!el) {
      onSubjectChange(subject + variable);
      return;
    }
    const start = el.selectionStart ?? subject.length;
    const end = el.selectionEnd ?? subject.length;
    const next = subject.slice(0, start) + variable + subject.slice(end);
    onSubjectChange(next);
    requestAnimationFrame(() => {
      el.selectionStart = start + variable.length;
      el.selectionEnd = start + variable.length;
      el.focus();
    });
  }

  function insertVariable(variable: string): void {
    if (lastFocused === 'subject') insertIntoSubject(variable);
    else insertIntoBody(variable);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm">{t('communications.emailSubject')}</Label>
        <Input
          ref={subjectRef}
          value={subject}
          onChange={(e) => {
            onSubjectChange(e.target.value);
          }}
          onFocus={() => {
            setLastFocused('subject');
          }}
          disabled={disabled}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm">{t('communications.emailBody')}</Label>
        <RichTextEditor
          ref={editorRef}
          value={body}
          onChange={onBodyChange}
          onFocus={() => {
            setLastFocused('body');
          }}
          disabled={disabled}
          placeholder={t('communications.emailBodyPlaceholder')}
        />
      </div>
      <TemplateVariableButtons
        variables={EMAIL_VARIABLES}
        onInsert={insertVariable}
        disabled={disabled}
      />
    </div>
  );
}
